import { Routes, Route, Navigate, useParams } from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { AdminRoute } from "./AdminRoute";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoginPage } from "@/features/auth/LoginPage";
import { SignupPage } from "@/features/auth/SignupPage";
import { SignupVerifyPage } from "@/features/auth/SignupVerifyPage";
import { AuthCompletePage } from "@/features/auth/AuthCompletePage";
import { LocalRecoveryPage, LocalRecoveryRequestPage } from "@/features/auth/LocalRecoveryPages";
import { ExternalSignupEmailVerifyPage, ExternalSignupPage } from "@/features/auth/ExternalSignupPages";
import { AccountSecurityPage } from "@/features/account/AccountSecurityPage";
import { AccountEmailVerifyPage } from "@/features/account/AccountEmailVerifyPage";
import { DashboardPage } from "@/features/dashboard/DashboardPage";
import { StorageListPage } from "@/features/storages/StorageListPage";
import { StorageDetailPage } from "@/features/storages/StorageDetailPage";
import { StorageNewPage } from "@/features/storages/StorageNewPage";
import { StorageBrowseResultPage } from "@/features/storages/StorageBrowseResultPage";
import { AppBindingListPage } from "@/features/app-bindings/AppBindingListPage";
import { AppBindingEditPage } from "@/features/app-bindings/AppBindingEditPage";
import { KintoneEnvironmentSwitchPage } from "@/features/app-bindings/KintoneEnvironmentSwitchPage";
import { AsynchronousExecutionsPage } from "@/features/asynchronous-executions/AsynchronousExecutionsPage";
import { AsynchronousExecutionDetailPage } from "@/features/asynchronous-executions/AsynchronousExecutionDetailPage";
import { AuditLogPage } from "@/features/audit-log/AuditLogPage";
import { DownloadsPage } from "@/features/downloads/DownloadsPage";
import { UsersPage } from "@/features/admin/UsersPage";
import { ServiceSubscriptionPage } from "@/features/admin/ServiceSubscriptionPage";
import { BillingReturnPage } from "@/features/billing/BillingReturnPage";
import { OrganizationSettingsPage } from "@/features/account/OrganizationSettingsPage";
import { OrganizationCreatePage } from "@/features/account/OrganizationCreatePage";
import { ContactPage } from "@/features/inquiries/ContactPage";
import { InvitationPage } from "@/features/account/InvitationPage";
import { AccountDeletedPage } from "@/features/account/AccountDeletedPage";
import { NativeUpdateAuthorizationPage } from "@/features/local-gateway/NativeUpdateAuthorizationPage";

function AppBindingDetailRoute() {
  const { kintoneAppId } = useParams<{ kintoneAppId?: string }>();
  if (!kintoneAppId || !/^[1-9][0-9]*$/.test(kintoneAppId)) {
    return <div role="alert">指定されたkintoneアプリは存在しません。</div>;
  }
  return <AppBindingEditPage />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/signup/verify" element={<SignupVerifyPage />} />
      <Route path="/signup/external" element={<ExternalSignupPage />} />
      <Route path="/auth/signup/external/email/verify" element={<ExternalSignupEmailVerifyPage />} />
      <Route path="/auth/local-recovery/request" element={<LocalRecoveryRequestPage />} />
      <Route path="/auth/local-recovery" element={<LocalRecoveryPage />} />
      <Route path="/auth/complete" element={<AuthCompletePage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/account-deleted" element={<AccountDeletedPage />} />

      <Route element={<ProtectedRoute />}>
        <Route path="/console/local-gateway-update" element={<NativeUpdateAuthorizationPage />} />
        <Route path="/invitations" element={<InvitationPage />} />
        <Route path="/console/billing/return" element={<BillingReturnPage />} />
        <Route element={<MainLayout requireOrganization={false} />}>
          <Route path="/console/account/security" element={<AccountSecurityPage />} />
        </Route>
        <Route path="/auth/account-email/verify" element={<AccountEmailVerifyPage />} />
        <Route element={<MainLayout />}>
          <Route path="/" element={<Navigate to="/console/dashboard" replace />} />
          <Route path="/console" element={<Navigate to="/console/dashboard" replace />} />
          <Route path="/console/dashboard" element={<DashboardPage />} />
          <Route path="/console/organizations/new" element={<OrganizationCreatePage />} />
          <Route path="/console/storages" element={<StorageListPage />} />
          <Route path="/console/storages/new/browse-result" element={<StorageBrowseResultPage />} />
          <Route path="/console/storages/new/:connectionKindId" element={<StorageNewPage />} />
          <Route path="/console/storages/new" element={<StorageNewPage />} />
          <Route path="/console/storages/:storageId" element={<StorageDetailPage />} />

          <Route path="/console/app-bindings" element={<AppBindingListPage key="list" />} />
          <Route path="/console/app-bindings/new/environment" element={<AppBindingListPage key="registration" initialRegistration />} />
          <Route path="/console/app-bindings/new" element={<AppBindingEditPage />} />
          <Route path="/console/app-bindings/:kintoneAppId" element={<AppBindingDetailRoute />} />

          <Route path="/console/asynchronous-executions" element={<AsynchronousExecutionsPage />} />
          <Route path="/console/asynchronous-executions/:executionId" element={<AsynchronousExecutionDetailPage />} />

          <Route path="/console/audit-log" element={<AuditLogPage />} />
          <Route path="/console/downloads" element={<DownloadsPage />} />

          <Route element={<AdminRoute />}>
            <Route path="/console/app-bindings/environment" element={<KintoneEnvironmentSwitchPage />} />
            <Route path="/console/organization/settings" element={<OrganizationSettingsPage />} />
            <Route path="/admin/users" element={<UsersPage />} />
            <Route path="/console/service-subscription" element={<ServiceSubscriptionPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
}
